<template>
  <nav
    class="w-full sticky top-0 isolate z-50 border-b backdrop-blur-2xl transition-colors duration-300"
    style="border-color: var(--border-subtle); background-color: color-mix(in srgb, var(--surface-bg) 78%, transparent); box-shadow: 0 1px 0 rgba(255, 255, 255, 0.04) inset;"
  >
    <div class="mx-auto flex h-16 w-full max-w-7xl items-center justify-between px-6 md:h-[72px]">
      <!-- Logo -->
      <NuxtLink to="/" class="flex items-center gap-2.5 group">
        <img
          src="/logo-2026-08.webp"
          alt="ososn"
          class="w-9 h-9 rounded-xl transition-transform duration-200"
          style="border: 1px solid var(--accent-border); box-shadow: 0 10px 24px -18px var(--accent-shadow-hover);"
        />
        <span class="text-[17px] font-semibold tracking-tight" translate="no" style="color: var(--accent);">ososn</span>
      </NuxtLink>

      <!-- Desktop Nav -->
      <div class="hidden md:flex items-center gap-1">
        <template v-for="item in navItems" :key="item.id">
          <!-- Dropdown: 推演工具 -->
          <div
            v-if="item.dropdown"
            ref="toolsMenuRef"
            class="relative"
            @mouseenter="toolsOpen = true"
            @mouseleave="toolsOpen = false"
          >
            <NuxtLink
              :to="localePath('/tools')"
              :no-prefetch="true"
              class="px-4 py-2 rounded-lg text-sm transition-all duration-200 inline-flex items-center gap-1.5 cursor-pointer"
              :class="isToolsActive
                ? 'font-medium'
                : 'hover:!text-[var(--text-primary)] hover:!bg-[var(--surface-card-hover)]'"
              :style="isToolsActive
                ? { color: 'var(--accent)', backgroundColor: 'var(--accent-bg)' }
                : { color: 'var(--text-faint)' }"
            >
              {{ item.label }}
              <UIcon name="i-heroicons-chevron-down" class="w-3.5 h-3.5 transition-transform" :class="{ 'rotate-180': toolsOpen }" />
            </NuxtLink>
            <Transition
              enter-active-class="transition-all duration-200 ease-out"
              enter-from-class="opacity-0 -translate-y-1 scale-95"
              enter-to-class="opacity-100 translate-y-0 scale-100"
              leave-active-class="transition-all duration-150 ease-in"
              leave-from-class="opacity-100 translate-y-0 scale-100"
              leave-to-class="opacity-0 -translate-y-1 scale-95"
            >
              <div
                v-show="toolsOpen"
                class="absolute right-0 mt-2 w-[860px] max-w-[calc(100vw-3rem)] rounded-2xl overflow-hidden z-50 border p-3"
                style="background-color: var(--surface-dropdown); border-color: var(--border-medium); box-shadow: var(--shadow-panel);"
              >
                <div
                  v-for="(toolGroup, groupIndex) in toolGroups"
                  :key="toolGroup.id"
                  class="grid min-w-0 gap-3 p-2 md:grid-cols-[210px_minmax(0,1fr)] md:gap-x-6"
                  :class="groupIndex < toolGroups.length - 1
                    ? 'border-b border-[var(--border-light)]'
                    : ''"
                >
                  <NuxtLink
                    :to="localePath(toolGroup.path)"
                    :no-prefetch="true"
                    class="group flex items-start gap-3 rounded-xl p-2 transition-colors hover:bg-[var(--surface-card-hover)]"
                    @click="toolsOpen = false"
                  >
                    <span class="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-[var(--accent-border)] bg-[var(--accent-bg)] text-[var(--accent)]">
                      <UIcon :name="toolGroup.icon" class="h-4 w-4" />
                    </span>
                    <span class="min-w-0">
                      <span class="block text-sm font-semibold" style="color: var(--text-primary);">
                        {{ t(toolGroup.titleKey) }}
                      </span>
                      <span class="mt-1 block text-xs leading-relaxed" style="color: var(--text-placeholder);">
                        {{ t(toolGroup.subtitleKey) }}
                      </span>
                    </span>
                    <UIcon name="i-heroicons-arrow-right" class="ml-auto mt-1 h-4 w-4 shrink-0 text-[var(--accent)] transition-transform duration-200 group-hover:translate-x-0.5" />
                  </NuxtLink>
                  <div class="flex flex-wrap content-start gap-1.5">
                    <NuxtLink
                      v-for="link in toolGroup.links"
                      :key="`${toolGroup.id}-${link.path}`"
                      :to="localePath(link.path)"
                      :no-prefetch="true"
                      class="flex h-8 max-w-full min-w-0 items-center gap-2 rounded-xl border border-[var(--border-light)] bg-[var(--surface-input)] px-3 text-[13px] transition-colors hover:border-[var(--border-medium)] hover:bg-[var(--surface-card-hover)]"
                      :class="route.path === localePath(link.path)
                        ? 'font-medium'
                        : 'hover:!bg-[var(--surface-card-hover)]'"
                      :style="route.path === localePath(link.path)
                        ? { color: 'var(--accent)', backgroundColor: 'var(--accent-bg)' }
                        : { color: 'var(--text-faint)' }"
                      @click="toolsOpen = false"
                    >
                      <span
                        class="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg transition-colors"
                        :style="route.path === localePath(link.path)
                          ? { backgroundColor: 'var(--accent-bg)', color: 'var(--accent)', border: '1px solid var(--accent-border)' }
                          : { backgroundColor: 'var(--surface-input)', color: 'var(--text-faint)', border: '1px solid var(--border-light)' }"
                      >
                        <UIcon :name="link.icon" class="h-3.5 w-3.5" />
                      </span>
                      <span class="truncate">{{ t(link.titleKey) }}</span>
                    </NuxtLink>
                  </div>
                </div>
              </div>
            </Transition>
          </div>

          <!-- Regular Link -->
          <NuxtLink
            v-else
            :to="localePath(item.to)"
            :no-prefetch="true"
            class="px-3.5 py-2 rounded-full text-sm transition-all duration-200 inline-flex items-center gap-1.5"
            style="color: var(--text-faint);"
            :class="route.path === localePath(item.to)
              ? 'font-medium'
              : 'hover:!text-[var(--text-primary)] hover:!bg-[var(--surface-card-hover)]'"
            :style="route.path === localePath(item.to)
              ? { color: 'var(--accent)', backgroundColor: 'var(--accent-bg)' }
              : {}"
          >
            {{ item.label }}
            <span
              v-if="item.badge"
              class="text-[10px] px-1.5 py-0.5 rounded font-medium leading-none"
              :style="{ backgroundColor: 'var(--accent)', color: '#fff' }"
            >{{ item.badge }}</span>
          </NuxtLink>
        </template>

        <!-- Theme Toggle -->
        <ThemeToggle class="ml-1" />

        <!-- Language Switcher -->
        <div ref="langSwitcherRef" class="relative ml-2">
          <button
            class="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs transition-all duration-200 border"
            style="color: var(--text-faint); border-color: var(--border-light);"
            :class="{ 'hover:!text-[var(--text-primary)] hover:!bg-[var(--surface-card-hover)]': true }"
            :aria-label="t('nav.language', { lang: currentLangLabel })"
            @click="langOpen = !langOpen"
          >
            <UIcon name="i-heroicons-language" class="w-3.5 h-3.5" />
            <span>{{ currentLangShort }}</span>
            <UIcon name="i-heroicons-chevron-down" class="w-3 h-3 transition-transform" :class="{ 'rotate-180': langOpen }" />
          </button>
          <Transition
            enter-active-class="transition-all duration-200 ease-out"
            enter-from-class="opacity-0 -translate-y-1 scale-95"
            enter-to-class="opacity-100 translate-y-0 scale-100"
            leave-active-class="transition-all duration-150 ease-in"
            leave-from-class="opacity-100 translate-y-0 scale-100"
            leave-to-class="opacity-0 -translate-y-1 scale-95"
          >
            <div
              v-if="langOpen"
              class="absolute right-0 mt-2 w-32 rounded-xl shadow-2xl overflow-hidden z-50 border"
              style="background-color: var(--surface-dropdown); border-color: var(--border-medium);"
            >
              <button
                v-for="lang in availableLanguages"
                :key="lang.code"
                class="w-full px-4 py-2.5 text-left text-sm transition-colors"
                :class="locale === lang.code
                  ? 'font-medium'
                  : 'hover:!bg-[var(--surface-card-hover)]'"
                :style="locale === lang.code
                  ? { color: 'var(--accent)', backgroundColor: 'var(--accent-bg)' }
                  : { color: 'var(--text-faint)' }"
                @click="switchLang(lang.code)"
              >
                {{ lang.name }}
              </button>
            </div>
          </Transition>
        </div>

        <!-- Auth (Google / Telegram sign-in) -->
        <AuthButton class="ml-2" />
      </div>

      <!-- Mobile Menu Button -->
      <UButton
        class="md:hidden"
        color="neutral"
        variant="ghost"
        size="sm"
        :icon="mobileOpen ? 'i-heroicons-x-mark' : 'i-heroicons-bars-3'"
        :aria-label="mobileOpen ? t('nav.closeMenu') : t('nav.openMenu')"
        @click="() => { mobileOpen = !mobileOpen }"
      />
    </div>

    <!-- Mobile Menu -->
    <Transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="opacity-0 -translate-y-2"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition-all duration-150 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-2"
    >
      <div
        v-if="mobileOpen"
        class="md:hidden border-t px-4 pb-6 pt-4 space-y-1 backdrop-blur-2xl max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain"
        style="border-color: var(--border-subtle); background-color: color-mix(in srgb, var(--surface-bg) 90%, transparent);"
      >
        <template v-for="item in navItems" :key="item.id">
          <!-- Dropdown: 推演工具 -->
          <div v-if="item.dropdown">
            <button
              class="flex items-center justify-between w-full px-3 py-3 rounded-xl text-sm transition-all"
              :class="isToolsActive ? 'font-medium' : ''"
              :style="isToolsActive
                ? { color: 'var(--accent)', backgroundColor: 'var(--accent-bg)' }
                : { color: 'var(--text-faint)' }"
              @click="mobileToolsOpen = !mobileToolsOpen"
            >
              <span class="flex items-center gap-2">
                {{ item.label }}
              </span>
              <UIcon name="i-heroicons-chevron-down" class="w-4 h-4 transition-transform" :class="{ 'rotate-180': mobileToolsOpen }" />
            </button>
            <Transition
              enter-active-class="transition-all duration-200 ease-out"
              enter-from-class="opacity-0 -translate-y-1"
              enter-to-class="opacity-100 translate-y-0"
              leave-active-class="transition-all duration-150 ease-in"
              leave-from-class="opacity-100 translate-y-0"
              leave-to-class="opacity-0 -translate-y-1"
            >
              <div v-show="mobileToolsOpen" class="pl-4 mt-1 space-y-3">
                <div v-for="toolGroup in toolGroups" :key="toolGroup.id">
                  <NuxtLink
                    :to="localePath(toolGroup.path)"
                    :no-prefetch="true"
                    class="flex items-center justify-between px-3 pb-1 text-[11px] font-semibold uppercase tracking-[0.12em]"
                    style="color: var(--text-placeholder);"
                    @click="mobileOpen = false; mobileToolsOpen = false"
                  >
                    <span>{{ t(toolGroup.titleKey) }}</span>
                    <UIcon name="i-heroicons-arrow-right" class="h-3.5 w-3.5" />
                  </NuxtLink>
                  <NuxtLink
                    v-for="link in toolGroup.links"
                    :key="`${toolGroup.id}-${link.path}`"
                    :to="localePath(link.path)"
                    :no-prefetch="true"
                    class="flex items-center gap-3 px-3 py-2 rounded-xl text-sm transition-colors"
                    :class="route.path === localePath(link.path)
                      ? 'font-medium'
                      : 'hover:!bg-[var(--surface-card-hover)]'"
                    :style="route.path === localePath(link.path)
                      ? { color: 'var(--accent)', backgroundColor: 'var(--accent-bg)' }
                      : { color: 'var(--text-faint)' }"
                    @click="mobileOpen = false; mobileToolsOpen = false"
                  >
                    <span
                      class="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                      :style="route.path === localePath(link.path)
                        ? { backgroundColor: 'var(--accent-bg)', color: 'var(--accent)', border: '1px solid var(--accent-border)' }
                        : { backgroundColor: 'var(--surface-input)', color: 'var(--text-faint)', border: '1px solid var(--border-light)' }"
                    >
                      <UIcon :name="link.icon" class="w-3.5 h-3.5" />
                    </span>
                    <span class="truncate">{{ t(link.titleKey) }}</span>
                  </NuxtLink>
                </div>
              </div>
            </Transition>
          </div>

          <!-- Regular Link -->
          <NuxtLink
            v-else
            :to="localePath(item.to)"
            :no-prefetch="true"
            class="flex items-center gap-2 px-3 py-3 rounded-xl text-sm transition-all"
            :class="route.path === localePath(item.to)
              ? 'font-medium'
              : 'hover:!text-[var(--text-primary)] hover:!bg-[var(--surface-card-hover)]'"
            :style="route.path === localePath(item.to)
              ? { color: 'var(--accent)', backgroundColor: 'var(--accent-bg)' }
              : { color: 'var(--text-faint)' }"
            @click="mobileOpen = false"
          >
            {{ item.label }}
            <span
              v-if="item.badge"
              class="text-[10px] px-1.5 py-0.5 rounded font-medium leading-none"
              :style="{ backgroundColor: 'var(--accent)', color: '#fff' }"
            >{{ item.badge }}</span>
          </NuxtLink>
        </template>

        <!-- Theme Toggle (Mobile) -->
        <div class="px-3 py-2">
          <ThemeToggle />
        </div>

        <!-- Auth (Mobile) -->
        <div class="border-t pt-2 mt-2" style="border-color: var(--border-subtle);">
          <p class="px-4 py-2 text-xs" style="color: var(--text-placeholder);">{{ isLoggedIn ? (user?.name || user?.email) : t('auth.signIn') }}</p>
          <template v-if="!isLoggedIn">
            <button
              class="flex items-center gap-2 w-full px-4 py-2.5 rounded-xl text-sm text-left transition-colors hover:!bg-[var(--surface-card-hover)]"
              style="color: var(--text-faint);"
              @click="mobileOpen = false; signInWithGoogle(route.fullPath || '/')"
            >
              <UIcon name="i-simple-icons-google" class="w-4 h-4" />
              {{ t('auth.signInWithGoogle') }}
            </button>
            <button
              class="flex items-center gap-2 w-full px-4 py-2.5 rounded-xl text-sm text-left transition-colors hover:!bg-[var(--surface-card-hover)]"
              style="color: var(--text-faint);"
              @click="mobileOpen = false; signInWithTelegram(route.fullPath || '/')"
            >
              <UIcon name="i-simple-icons-telegram" class="w-4 h-4" />
              {{ t('auth.signInWithTelegram') }}
            </button>
          </template>
          <template v-else>
            <NuxtLink
              :to="localePath('/settings')"
              :no-prefetch="true"
              class="flex items-center gap-2 w-full px-4 py-2.5 rounded-xl text-sm text-left transition-colors hover:!bg-[var(--surface-card-hover)]"
              style="color: var(--text-faint);"
              @click="mobileOpen = false"
            >
              <UIcon name="i-heroicons-folder-open" class="w-4 h-4" />
              {{ t('nav.profiles') }}
            </NuxtLink>
            <button
              class="flex items-center gap-2 w-full px-4 py-2.5 rounded-xl text-sm text-left transition-colors hover:!bg-[var(--surface-card-hover)]"
              style="color: var(--text-faint);"
              @click="mobileOpen = false; signOutUser()"
            >
              <UIcon name="i-heroicons-arrow-right-on-rectangle" class="w-4 h-4" />
              {{ t('auth.signOut') }}
            </button>
          </template>
        </div>

        <div class="border-t pt-2 mt-2" style="border-color: var(--border-subtle);">
          <p class="px-4 py-2 text-xs" style="color: var(--text-placeholder);">{{ $t('nav.languageLabel') }}</p>
          <button
            v-for="lang in availableLanguages"
            :key="lang.code"
            class="block w-full px-4 py-2.5 rounded-xl text-sm text-left transition-colors"
            :class="locale === lang.code
              ? 'font-medium'
              : 'hover:!bg-[var(--surface-card-hover)]'"
            :style="locale === lang.code
              ? { color: 'var(--accent)', backgroundColor: 'var(--accent-bg)' }
              : { color: 'var(--text-faint)' }"
            @click="switchLang(lang.code); mobileOpen = false"
          >
            {{ lang.name }}
          </button>
        </div>
      </div>
    </Transition>
  </nav>
</template>

<script setup lang="ts">
const route = useRoute()
const router = useRouter()
const { locale, t } = useI18n()
const localePath = useLocalePath()
const switchLocalePath = useSwitchLocalePath()
const mobileOpen = ref(false)
const langOpen = ref(false)
const { user, isLoggedIn, signInWithGoogle, signInWithTelegram, signOutUser } = useAuth()
const toolsOpen = ref(false)
const mobileToolsOpen = ref(false)

// Lock page scroll while the mobile menu is open so touches inside the menu
// scroll the menu, not the body behind it.
watch(mobileOpen, (open) => {
  if (!import.meta.client) return
  document.body.style.overflow = open ? 'hidden' : ''
})

onBeforeUnmount(() => {
  if (import.meta.client) document.body.style.overflow = ''
})

const categories = useToolCategories()
const toolGroups = useToolDirectoryGroups()

const isToolsActive = computed(() => {
  const toolPaths = toolGroups.value.map(group => localePath(group.path))
  toolPaths.push(...categories.value.map(c => localePath(c.sectionPath)))
  return toolPaths.some(p => route.path === p)
})

interface NavItem {
  label: string
  to: string
  id: string
  dropdown?: boolean
  badge?: string
}

const navItems = computed<NavItem[]>(() => [
  { label: t('nav.home'), to: '/', id: 'nav-home' },
  { label: t('nav.todayAlmanac'), to: '/tools/jinri-huangli', id: 'nav-today-almanac' },
  { label: t('nav.toolsShort'), to: '/tools', dropdown: true, id: 'nav-tools-dropdown' },
  { label: t('nav.shelf'), to: '/shelf', id: 'nav-shelf' },
  { label: t('nav.graph'), to: '/graph', id: 'nav-graph' },
  { label: t('nav.insights'), to: '/insights', id: 'nav-insights' },
  { label: t('nav.wiki'), to: '/wiki', id: 'nav-wiki' },
])

type AppLanguageCode = 'zh-CN' | 'zh-TW' | 'en' | 'ja' | 'ko'

const baseLanguages = computed<Array<{ code: AppLanguageCode, name: string }>>(() => [
  { code: 'zh-CN', name: t('language.zhCN') },
  { code: 'zh-TW', name: t('language.zhTW') },
  { code: 'en', name: t('language.en') },
  { code: 'ja', name: t('language.ja') },
  { code: 'ko', name: t('language.ko') },
])

const availableLanguages = computed(() => baseLanguages.value)

const currentLangLabel = computed(() => {
  return availableLanguages.value.find(l => l.code === locale.value)?.name || t('language.zhCN')
})

const langShortMap: Record<string, string> = {
  'zh-CN': t('languageShort.zhCN'),
  'zh-TW': t('languageShort.zhTW'),
  'en': t('languageShort.en'),
  'ja': t('languageShort.ja'),
  'ko': t('languageShort.ko'),
}
const currentLangShort = computed(() => langShortMap[locale.value] || '简')

function switchLang(code: AppLanguageCode) {
  const newPath = switchLocalePath(code)
  langOpen.value = false
  if (newPath) {
    router.push(newPath)
  }
}

// 点击外部关闭语言下拉
const langSwitcherRef = ref<HTMLElement | null>(null)

// 点击外部关闭工具下拉
const toolsMenuRef = ref<HTMLElement | null>(null)

function handleDocClick(e: MouseEvent) {
  if (langSwitcherRef.value && !langSwitcherRef.value.contains(e.target as Node)) {
    langOpen.value = false
  }
  const toolsEl = Array.isArray(toolsMenuRef.value) ? toolsMenuRef.value[0] : toolsMenuRef.value
  if (toolsEl && !toolsEl.contains(e.target as Node)) {
    toolsOpen.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleDocClick)
})
onBeforeUnmount(() => {
  document.removeEventListener('click', handleDocClick)
})
</script>
